import * as React from 'react';

import { compose } from 'recompose';

import mapProps from 'js/lib/mapProps';

import { useAuditUpgradeFlowData } from 'bundles/audit/contexts/AuditUpgradeFlowDataContext';
import { isItemLockedToAuditUpgradeFlowUsers } from 'bundles/audit/utils/isLocked';
import { Tooltip } from 'bundles/common/components/Tooltip';
import type { Placement } from 'bundles/common/components/Tooltip';
import withIsScheduleIncentivesEnabled from 'bundles/course-home/page-course-week/utils/withIsScheduleIncentivesEnabled';
import withSessionLabel from 'bundles/course-sessions/utils/withSessionLabel';
import type { SessionLabel } from 'bundles/course-sessions/utils/withSessionLabel';
import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import { useIsFreemiumLocked } from 'bundles/enroll-common/utils/freemiumModelUtils';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getFormattedLockMessage } from 'bundles/learner-progress/utils/Item';

import _t from 'i18n!nls/learner-progress';

type PropsFromCaller = {
  computedItem: Item;
  placement: Placement;
  showDistinctAvailabilityReviewDates?: boolean;
};

type PropsFromHocs = {
  sessionsV2EnrollmentEnabled: boolean;
  sessionLabel: SessionLabel;
  isScheduleIncentivesEnabled: boolean;
};

type PropsToComponent = PropsFromCaller & PropsFromHocs;

const LockedTooltip = (props: PropsToComponent) => {
  const {
    sessionLabel,
    computedItem: item,
    placement = 'right',
    sessionsV2EnrollmentEnabled,
    isScheduleIncentivesEnabled,
    showDistinctAvailabilityReviewDates,
  } = props;
  const auditUpgradeFlowDataContext = useAuditUpgradeFlowData();
  const isFreemiumLocked = useIsFreemiumLocked({
    isFreemiumLearner: auditUpgradeFlowDataContext?.isFreemiumLearner,
    moduleNumber: item.weekNumber,
  });

  if (!item.itemLockedStatus) {
    return null;
  }

  // Course Frame: This is duplicated to static/bundles/course-frame/components/course-outline/NavSingleItemDisplayV2.tsx
  const { itemLockedStatus, itemLockedReasonCode, itemLockSummary, deadline } = item;

  const formattedLockMessageOptions = {
    sessionsV2Enabled: sessionsV2EnrollmentEnabled,
    sessionLabel,
    itemLockedStatus,
    itemLockedReasonCode,
    itemLockSummary,
    isScheduleIncentivesEnabled,
    deadline,
    showDistinctAvailabilityReviewDates,
    isFreemiumLocked,
  };

  const message = getFormattedLockMessage(formattedLockMessageOptions);

  // Conditionally setting the margin-top style based on the message and the screen size, otherwise the longer messages will start
  // interfering with other items in the navbar
  const tooltipStyle =
    message !== 'This item is locked.' &&
    item.itemLockedReasonCode !== 'PASSABLE_ITEM_COMPLETION' &&
    window.innerWidth >= 1024
      ? { marginTop: '-68px' }
      : {};

  return (
    <Tooltip
      className="locked-tooltip"
      style={tooltipStyle}
      arrowClassName="locked-tooltip-arrow"
      placement={placement}
    >
      {auditUpgradeFlowDataContext && isItemLockedToAuditUpgradeFlowUsers(item) ? _t('Upgrade to unlock') : message}
    </Tooltip>
  );
};

// Course Frame: This HOC chain is duplicated to static/bundles/course-frame/components/course-outline/NavSingleItemDisplayV2.tsx
export default compose<PropsToComponent, PropsFromCaller>(
  withSessionsV2EnrollmentEnabled((propsFromCaller) => propsFromCaller.computedItem.courseId),
  mapProps((propsFromCaller: PropsFromCaller) => ({
    courseId: propsFromCaller.computedItem.courseId,
  })),
  withSessionLabel,
  withIsScheduleIncentivesEnabled
)(LockedTooltip);
